import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Wallet, ChevronRight } from "lucide-react";

/* Invoice ageing summary — visible to ALL internal users (admin / CM / team).
   Reads /api/finance/invoice-ageing which is gated to internal users.
   Numbers come straight from finance_revenue; paid/cancelled are excluded. */
const BUCKET_TONES = {
  "current": "slate", "0-30": "blue", "31-60": "yellow", "61-90": "orange", "90+": "red",
};
const BUCKET_LABELS = {
  "current": "Not due", "0-30": "0–30 days", "31-60": "31–60", "61-90": "61–90", "90+": "90+",
};

function fmt(n) { return `₹${Number(n || 0).toLocaleString("en-IN")}`; }

export default function InvoiceAgeingWidget() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/finance/invoice-ageing")
       .then((r) => { if (alive) setData(r.data); })
       .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Couldn't load ageing"); });
    return () => { alive = false; };
  }, []);

  if (err) {
    return (
      <div className="card-flat p-4" data-testid="invoice-ageing-widget-error">
        <div className="label-overline mb-1 inline-flex items-center gap-1.5"><Wallet size={11} /> Invoice ageing</div>
        <div className="text-xs text-slate-500">{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card-flat p-4" data-testid="invoice-ageing-widget-loading">
        <div className="label-overline mb-1 inline-flex items-center gap-1.5"><Wallet size={11} /> Invoice ageing</div>
        <div className="text-xs text-slate-400">Loading…</div>
      </div>
    );
  }

  const overdueAmt = (data.buckets || []).filter((b) => b.key !== "current").reduce((s, b) => s + b.amount, 0);
  const top = (data.items || []).filter((i) => i.bucket !== "current").slice(0, 4);

  return (
    <div className="card-flat overflow-hidden" data-testid="invoice-ageing-widget">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet size={14} className="text-[#F54800]" />
          <div>
            <div className="font-display font-bold text-sm text-slate-900">Invoice ageing</div>
            <div className="text-[10px] text-slate-500">Outstanding {fmt(data.total_outstanding)} · Overdue {fmt(overdueAmt)}</div>
          </div>
        </div>
        <Link to="/studio?tab=finance&sub=invoices" className="text-[11px] text-[#F54800] hover:underline inline-flex items-center gap-0.5">
          Open <ChevronRight size={12} />
        </Link>
      </div>
      {/* Bucket grid */}
      <div className="grid grid-cols-5 divide-x divide-slate-100">
        {(data.buckets || []).map((b) => (
          <div key={b.key} className="px-3 py-3" data-testid={`ageing-bucket-${b.key}`}>
            <Tag tone={BUCKET_TONES[b.key]}>{BUCKET_LABELS[b.key]}</Tag>
            <div className={`font-display text-base font-bold mt-1 ${b.key === "90+" && b.amount > 0 ? "text-red-700" : b.key === "61-90" && b.amount > 0 ? "text-orange-700" : "text-slate-900"}`}>
              {fmt(b.amount)}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5">{b.count} invoice{b.count === 1 ? "" : "s"}</div>
          </div>
        ))}
      </div>
      {/* Top overdue list */}
      {top.length > 0 && (
        <div className="border-t border-slate-100 px-5 py-3">
          <div className="label-overline mb-2">Most overdue</div>
          <ul className="space-y-1.5">
            {top.map((it, idx) => (
              <li key={it.id || `${it.invoice_number}-${idx}`} className="flex items-center justify-between text-xs" data-testid={`ageing-item-${it.id || it.invoice_number}-${idx}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-800 truncate font-medium">{it.client_name || "—"}</div>
                  <div className="text-[10px] text-slate-500 truncate font-mono">{it.invoice_number} · {it.project_name || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-slate-900">{fmt(it.amount)}</div>
                  <div className="text-[10px] text-red-600 font-semibold">{it.days_overdue}d overdue</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
